import { IconButton, Tooltip } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import {
  addIntelligenceComment as apiAddIntelligenceComment,
  deleteIntelligenceComment as apiDeleteIntelligenceComment,
} from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Dropdown } from '~/components2/Dropdown';
import EmailLink from '~/components2/EmailLink';
import Form from '~/components2/forms/Form';
import Icon from '~/components2/Icon';
import { getCircleBgColor } from '~/components2/Map/canvas/comment/common';
import { Notify } from '~/components2/Notify';
import Popover from '~/components2/Popover/Popover';
import { getTimeDifference } from '~/helpers/timeDifference';
import type { ProjectDetailsParams } from '../..';
import store from '../store';
import type { IntelligenceV2 } from '.';

interface CommentProps {
  featureId: string;
}

export default function CommentComponent({ featureId }: CommentProps) {
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { projectIntelligence } = useSnapshot(store) as typeof store;
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const commentList = useMemo(() => {
    if (projectIntelligence[featureId]) {
      const comments = projectIntelligence[featureId].comments || [];

      if (!comments || comments.length === 0) {
        return null;
      }

      return comments.map((c, i) => {
        const initial = c.createdBy.name.charAt(0).toUpperCase();
        const formattedDate = DateTime.fromISO(c.createdAt.toString(), {
          outputCalendar: 'unix',
        });

        return (
          <div key={c.id} className="">
            <div className="flex items-start justify-between p-4">
              <div className="flex gap-2">
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full ${getCircleBgColor({
                    isComponentRequest: true,
                    initial,
                  })}`}
                >
                  <span className="text-xs font-bold text-white">{c.createdBy.name ? initial : ''}</span>
                </div>
                <div className="flex flex-1 flex-col gap-1">
                  <span className="text-xs font-bold">{c.createdBy.name}</span>
                  <span className="text-xs text-grey-50">{`${getTimeDifference(formattedDate)} ago`}</span>
                  <span className="whitespace-pre text-xs">{c.comment}</span>
                </div>
              </div>

              <Dropdown
                align="end"
                sideOffset={18}
                alignOffset={-18}
                triggerElement={
                  <button
                    type="button"
                    aria-label={`Comment actions for ${c.createdBy.name}`}
                    data-testid={`intelligence-comment-actions-${c.id}`}
                    className="appearance-none border-0 bg-transparent p-0 rounded focus-ring"
                  >
                    <Icon icon="moreHorizontal" size={18} className="cursor-pointer" />
                  </button>
                }
                items={[
                  {
                    type: 'simple',
                    label: 'Delete comment',
                    onClick: () => {
                      onDelete(c.id);
                    },
                  },
                ]}
              />
            </div>
            {i !== comments.length - 1 && <hr className="m-0 w-full border-grey-20" />}
          </div>
        );
      });
    }

    return null;
  }, [projectIntelligence[featureId]]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!message || message === '' || !featureId) {
      return;
    }

    try {
      setIsLoading(true);
      setIsError(false);

      const res = await apiAddIntelligenceComment<IntelligenceV2>(projectId, featureId, message);

      if (!res.id) {
        throw new Error('Error adding intelligence comment.');
      }

      if (res.comments) {
        store.projectIntelligence[featureId].comments = [...res.comments];
      }

      if (store.project && store.projectIntelligence[featureId]) {
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceCommentAdded, {
          project_id: store.project.id,
          organization_id: store.project.organization,
          feature_id: featureId,
          comment_id: res.id,
          layer_id: store.projectIntelligence[featureId].layerId,
        });
      }

      setMessage('');

      Notify.info({
        title: 'Add Comment',
        message: 'Comment successfully added.',
      });
    } catch {
      setIsError(true);

      Notify.error({
        title: 'Add Comment',
        message: `An error occurred while adding your comment.`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onDelete = async (commentId: string) => {
    try {
      const res = await apiDeleteIntelligenceComment<IntelligenceV2>(projectId, featureId, commentId);

      if (!res.id) {
        throw new Error('Error deleting comment.');
      }

      if (res.comments) {
        store.projectIntelligence[featureId].comments = [...res.comments];
      }

      if (store.project && store.projectIntelligence[featureId]) {
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceCommentArchived, {
          project_id: store.project.id,
          organization_id: store.project.organization,
          comment_id: commentId,
          feature_id: featureId,
          layer_id: store.projectIntelligence[featureId].layerId,
        });
      }

      Notify.info({
        title: 'Delete Comment',
        message: 'Comment successfully deleted.',
      });
    } catch {
      Notify.error({
        title: 'Delete Comment',
        message: 'There was an error deleting your comment. Please contact support.',
      });
    }
  };

  const commentCount = projectIntelligence[featureId]?.comments?.length ?? 0;

  return (
    <div className="flex items-center justify-center">
      <Popover
        side="left"
        align="end"
        sideOffset={10}
        trigger={
          <Tooltip side="right" sideOffset={10} text="View comments" asChild>
            <button
              type="button"
              aria-label={commentCount ? `View comments (${commentCount})` : 'View comments'}
              data-testid={`intelligence-comments-trigger-${featureId}`}
              className="flex appearance-none border-0 bg-transparent p-0 rounded focus-ring"
            >
              <Icon icon={commentCount ? 'commentFilled' : 'comment'} />
            </button>
          </Tooltip>
        }
      >
        {(closePopover) => {
          return (
            <div className="flex w-full min-w-[16rem] max-w-xs flex-col justify-between rounded border bg-white p-1">
              <div className="max-h-52 overflow-y-auto">{commentList}</div>

              <Form onSubmit={onSubmit} className="flex items-center justify-between space-x-2">
                {isError ? (
                  <div className="p-1 text-2xs text-primary">
                    An error occurred while adding your comment. Please <EmailLink>contact support</EmailLink>.
                  </div>
                ) : (
                  <textarea
                    placeholder="Enter a comment..."
                    name="Intelligence Comment"
                    id="intelligence-comment"
                    data-testid="intelligence-comment-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="grow resize-none border p-1 text-xs text-primary"
                    rows={2}
                  />
                )}

                <Tooltip asChild text="Post comment">
                  <IconButton
                    variant="secondary"
                    icon="envelopeClosed"
                    type="submit"
                    disabled={!message || isError}
                    loading={isLoading}
                    aria-label="Post comment"
                    data-testid="intelligence-comment-submit"
                  />
                </Tooltip>
              </Form>
            </div>
          );
        }}
      </Popover>
    </div>
  );
}
